// Shared helpers for the chat UI.

import type {
  ChatChannel,
  ChatConversation,
  ChatMessage,
  ChatFileType,
} from "../../types/chat";

export type ChatRole = "retailer" | "sales" | "distributor";

// The current user's own id, read from the JWT `id` claim (sales = WebUser id,
// retailer = C_Number, distributor = PM_ID). Used to identify "my" messages in a
// group where every sender is 'sales'.
export const decodeSelfId = (): string | null => {
  try {
    const token = localStorage.getItem("token") || "";
    const part = token.split(".")[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return json?.id != null ? String(json.id) : null;
  } catch {
    return null;
  }
};

// Determine whether a message was sent by the current user.
// - retailer: their own 'retailer'/'storeManager' messages (all sales are "them")
// - sales in retailer_sales: only MY OWN sales messages. A thread can now involve
//   two sales people (transfers), so "any sales message" is no longer "mine" — we
//   compare senderId to selfId, exactly like the sales_sales case.
// - sales in sales_sales (direct OR group): compare senderId to selfId; fall back to
//   the direct counterpart if selfId is unavailable
// - distributor: read-only, nothing is "mine"
export const isOwnMessage = (
  message: ChatMessage,
  role: ChatRole | null,
  channel: ChatChannel,
  conversation: ChatConversation | null,
  selfId?: string | null
): boolean => {
  if (!role) return false;
  // The admin posts only in the groups they own, and those messages carry
  // senderType 'admin' — everything else they see belongs to someone else.
  if (role === "distributor") {
    return message.senderType === "admin" && String(message.senderId) === String(selfId ?? "");
  }
  if (role === "retailer") {
    return message.senderType === "retailer" || message.senderType === "storeManager";
  }
  // sales, retailer_sales channel: mine = a sales message I sent myself.
  if (channel === "retailer_sales") {
    if (message.senderType !== "sales") return false;
    // No id to compare against (shouldn't happen for an authed sales user) — fall
    // back to the old sole-rep behavior so at least single-handler chats align.
    if (selfId == null) return true;
    return String(message.senderId) === String(selfId);
  }
  // sales_sales — direct or group
  if (selfId != null) {
    return String(message.senderId) === String(selfId);
  }
  if (conversation?.counterpartId != null) {
    return String(message.senderId) !== String(conversation.counterpartId);
  }
  return false;
};

// Build an in-app product-list link for the CURRENT viewer.
//
// /retailer/order and /sales/order are role-locked (RoleBasedRoute rejects the wrong
// one), and a promo broadcast is seen by retailers and sales alike — so the path has
// to be built from the viewer's role at render time. Storing one absolute URL, as the
// notification system does, would 403 for half the audience.
export const productListPathFor = (
  role: ChatRole | null | undefined,
  itemNumbers: string[] | null | undefined
): string => {
  const base = role === "retailer" ? "/retailer/order" : "/sales/order";
  const csv = (itemNumbers ?? []).map((i) => String(i).trim()).filter(Boolean).join(",");
  return csv ? `${base}?masterSearch=${encodeURIComponent(csv)}` : base;
};

// Short, human-friendly time for a message row.
export const formatMessageTime = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Relative-ish timestamp for the conversation list.
export const formatListTime = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

// "Today" / "Yesterday" / "Mar 5" style label for date separators.
export const formatDayLabel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
};

export const isSameDay = (a: string, b: string): boolean => {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

// Up-to-two-letter initials for the avatar. Titles carry trailing decoration like
// "ASHOKBHAI (#34008)", so drop punctuation and number-only words — otherwise the
// last "word" is "(#34008)" and the avatar reads "A(".
export const initials = (name: string | null | undefined): string => {
  if (!name) return "?";
  const words = name
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter((w) => w && /^\p{L}/u.test(w));

  if (!words.length) {
    const fallback = name.replace(/[^\p{L}\p{N}]/gu, "")[0];
    return (fallback || "?").toUpperCase();
  }

  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : "";
  return (first + last).toUpperCase();
};

// Deterministic pleasant color for a name/id so each contact keeps its hue.
const AVATAR_COLORS = [
  "#3C7795", "#7E57C2", "#EF6C00", "#2E7D32",
  "#C2185B", "#00838F", "#5D4037", "#455A64",
];
export const avatarColor = (seed: string): string => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

export const humanFileSize = (bytes: number): string => {
  if (!bytes) return "";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

// Merge/append a message into a list without duplicating by id.
export const upsertMessage = (
  list: ChatMessage[],
  message: ChatMessage
): ChatMessage[] => {
  const idx = list.findIndex((m) => m.id === message.id);
  if (idx === -1) return [...list, message];
  const next = list.slice();
  next[idx] = message;
  return next;
};

export const fileTypeLabel: Record<ChatFileType, string> = {
  image: "Image",
  pdf: "PDF",
  doc: "Document",
  video: "Video",
  other: "File",
};

// Extract a friendly error message from an axios error / chat error envelope.
export const chatErrorMessage = (err: any, fallback = "Something went wrong"): string => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 403) return "You don't have access to this chat.";
  if (status === 409) return data?.message || "This chat has been ended.";
  return data?.message || data?.error || err?.message || fallback;
};

// Chat uploads are capped well below the backend's hard limit so users get a clear
// warning instead of a failed request. Returns the files under the cap, plus the
// names of any that were too big (for a toast).
export const MAX_CHAT_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
export const filterOversizeFiles = (
  files: File[]
): { ok: File[]; rejected: string[] } => {
  const ok: File[] = [];
  const rejected: string[] = [];
  for (const f of files) {
    if (f.size > MAX_CHAT_FILE_BYTES) rejected.push(f.name);
    else ok.push(f);
  }
  return { ok, rejected };
};
